import { api } from '../utils/apiClient';

export type NearMissMetric = 'latency' | 'error_rate' | 'cpu' | 'request_rate';
export interface NearMissRule {
  id: string;
  metric: NearMissMetric;
  enabled: boolean;
  breach: number;
  entry: number;
  recovery: number;
  duration_seconds: number;
  minimum_count: number;
  recovery_seconds: number;
  recovery_count: number;
  minimum_traffic: number | null;
  max_source_age_seconds: number;
  max_evidence_age_seconds: number;
  aggregation: 'max_component_p95' | 'ratio_of_sums' | 'sum' | 'max' | 'mean';
}
export interface NearMissConfig {
  version: 1;
  detectionEnabled: boolean;
  selectionInfluenceEnabled: boolean;
  automaticObservationEnabled?: boolean;
  observationCadenceSeconds?: number;
  windowMinutes: number;
  projectRules: NearMissRule[];
  serviceRules: Record<string, NearMissRule[]>;
}
export interface RuntimeInventory {
  version?: 1;
  source: string;
  dataset: 'synthetic' | 'measured';
  services: { id: string; components: string[]; depends_on?: string[] }[];
}
export interface NearMissEpisode {
  id: string;
  service?: string;
  metric?: NearMissMetric;
  rule_revision?: number;
  started_at: number;
  confirmed_at?: number;
  last_observed_at?: number;
  closed_at?: number;
  recovery_started_at?: number;
  qualifying_seconds?: number;
  gap_seconds?: number;
  outcome: string;
  interrupted?: boolean;
  had_gaps?: boolean;
  left_censored?: boolean;
  rule?: NearMissRule | null;
  provenance?: { provider?: string; source?: string; dataset?: string } | null;
}
export type NearMissState = 'normal' | 'active_near_miss' | 'recovered_without_observed_breach' | 'breached' | 'insufficient_evidence';
export interface NearMissResult {
  slot_rejections?: Record<string, string>;
  service: string;
  metric: NearMissMetric;
  state: NearMissState;
  reason?: string;
  current_condition?: string;
  last_condition?: string;
  rule: NearMissRule;
  rule_revision: number;
  value?: number;
  distance?: number;
  relative_distance?: number;
  qualifying_count?: number;
  qualifying_seconds?: number;
  last_observed_value?: number;
  last_observed_at?: number;
  expected_slots?: number;
  observed_slots?: number;
  coverage?: number;
  episode?: NearMissEpisode | null;
  last_episode?: NearMissEpisode | null;
}
export interface TemporalQuality {
  freshness_rejection?: string | null;
  source_updated_at?: number | null;
  source_age_kind?: 'measured' | 'upper_bound';
  status?: string;
  source_age_seconds?: number | null;
  freshness_contract?: string | null;
  support_seconds?: number;
  statistic?: string;
  canonical_unit?: string;
  meaning?: string;
  freshness_evidence?: string;
}
export interface NearMissEvaluation {
  observation?: { enabled: boolean; cadence_seconds: number; latest_attempt_at?: number; latest_success_at?: number; latest_completed_at?: number; status?: string; failure_code?: string | null; evidence_state?: string; lease_until?: number };
  evaluation_id?: string;
  evaluated_at?: number;
  detection_enabled: boolean;
  selection_influence_enabled?: boolean;
  mode?: string;
  evaluator_version?: string;
  state?: NearMissState;
  reason?: string;
  results: NearMissResult[];
  evidence?: {
    provider?: string;
    source?: string;
    dataset?: string;
    failure_code?: string;
    slot_ends?: number[];
    acquisition?: { failure_code?: string; freshness_evidence?: string; profile_version?: number; resolution?: string };
    slots?: Record<string, Record<string, { value: number | null; reason: string; expected_components?: number; observed_components?: number }>>;
    observations?: Record<string, (Partial<Record<NearMissMetric, number | null>> & { quality?: Partial<Record<NearMissMetric, TemporalQuality>> })[]>;
  };
}
export interface NearMissEpisodePage { items: NearMissEpisode[]; limit: number; offset: number }

/** Frozen run explanations, carried by the existing persisted reasons JSON. */
export interface NearMissContribution {
  influence_enabled: boolean; eligible: boolean; applied: boolean; contributed_to_candidate: boolean;
  candidate_before: boolean; candidate_after: boolean; reason?: string; service?: string;
  evaluation_id?: string; episode_ids?: string[]; decision_cutoff?: number;
  provider?: string; source?: string; dataset?: string; observation_interval?: number[];
  rules?: { metric: NearMissMetric; rule_revision?: number; episode_id?: string; eligible: boolean; reason: string }[];
  selected?: boolean; baseline_selected?: boolean | null; selection_changed?: boolean;
  comparison_test_id?: number | null;
  comparison_anchor_test_id?: number | null;
}
export interface NearMissComparison {
  project_id?: string;
  evaluation_id?: string; decision_cutoff: number; method: string;
  baseline_selected_ids: number[]; selected_ids: number[]; added_test_ids: number[]; removed_test_ids: number[];
}

const projectQuery = (projectId: string) => `project_id=${encodeURIComponent(projectId)}`;
export const getNearMissStatus = (projectId: string) =>
  api.get<NearMissEvaluation>(`/near-miss/status?${projectQuery(projectId)}`);
export const listNearMissEpisodes = (projectId: string, offset = 0, limit = 20) =>
  api.get<NearMissEpisodePage>(`/near-miss/episodes?${projectQuery(projectId)}&limit=${limit}&offset=${offset}`);
export const getNearMissEvaluation = (projectId: string, evaluationId: string) =>
  api.get<NearMissEvaluation>(`/near-miss/evaluations/${encodeURIComponent(evaluationId)}?${projectQuery(projectId)}`);
export const evaluateNearMiss = (projectId: string, requestKey: string) =>
  api.post<NearMissEvaluation>(`/near-miss/evaluate?${projectQuery(projectId)}`, { requestKey });

export interface SavedNearMissExplanationData {
  test_count?: number;
  tests_page?: { page: number; page_size: number; total: number; filtered_count: number; total_pages: number };
  run_id: string;
  project_id: string;
  influence_enabled: boolean | null;
  comparison_status: 'complete' | 'partial' | 'unavailable';
  counterfactual_context?: {
    model_version?: string; base_context_snapshot_id?: string;
    actual_threshold?: number; influence_off_threshold?: number; tau?: number;
  } | null;
  outcome: string;
  counts: Record<string, number | null>;
  rf_counts?: Record<string, number | null>;
  limitations: string[];
  evaluation_ids?: string[];
  tests: {
    test_id: number | null; test_identifier: string; service?: string | null;
    selected: boolean | null; baseline_selected: boolean | null;
    classification: string; eligible?: boolean | null; promoted: boolean | null; retained: boolean | null;
    effect_classification?: string;
    probability_off?: number | null; probability_on?: number | null; probability_changed?: boolean;
    rank_off?: number | null; rank_on?: number | null;
    inclusion_rule_off?: string | null; inclusion_rule_on?: string | null;
    coverage_guardrail_off?: boolean; coverage_guardrail_on?: boolean;
    threshold_off?: number | null; threshold_on?: number | null;
    selection_change_reason?: string | null;
    feature_changes?: { feature: string; off?: string | number | boolean | null; on?: string | number | boolean | null }[];
    reason?: string | null; supporting_alternative: boolean;
    evaluation_id?: string | null; provider?: string | null; source?: string | null; dataset?: string | null;
    decision_cutoff?: number | null; observation_interval?: (number | null)[] | null;
    rules: { metric?: string | null; eligible?: boolean | null; reason?: string | null; episode_id?: string | null; rule_revision?: number | null; scope_id?: string | null }[];
  }[];
}
